import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Checkbox } from "semantic-ui-react";
import { AppState } from "../../store/configureStore";
import { RedirectIfLoggedIn } from "../../components/RedirectIfLoggedIn";
import { loginRequest } from "../../actions/authentificationActions";
import { fetchAppStage } from "../../store/appStageReducer";
import logo from "./tscnet-logo.png";
//@ts-ignore
import Pdf from "../../documents/GDPR_Perun_Documentation_20240519.pdf";
//@ts-ignore
import UseStatementPdf from "../../documents/TSCNET_Services_GmbH_PERUN4V_Acceptable_Use_Statement_20240519.pdf";
import { toast } from "../..";
import { Configuration, PublicClientApplication } from "@azure/msal-browser";
import {
    getActiveDirectorySettings,
    isLocalLoginEnabled,
} from "../../services/authenticationService";
import { useNavigate } from "react-router-dom";
import "./Login.css";

const loginRequestObj = {
    scopes: ["User.Read"],
};

export const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const login = useSelector((state: AppState) => state.login);
    const stageState = useSelector((state: AppState) => state.appStage);

    const [title, setTitle] = useState("");
    const [msalConfig, setMsalConfig] = useState<Configuration>();
    const [usePolicyAccepted, setUsePolicyAccepted] = useState(false);
    const [localLoginEnabled, setLocalLoginEnabled] = useState(false);

    const msalInstance = useMemo(() => {
        if (!msalConfig) return;

        return new PublicClientApplication(msalConfig);
    }, [msalConfig]);

    useEffect(() => {
        isLocalLoginEnabled().then(enabled => {
            setLocalLoginEnabled(enabled);
        });
    }, [localLoginEnabled]);

    useEffect(() => {
        fetchAppStage(dispatch);
    }, [dispatch]);

    useEffect(() => {
        try {
            switch (stageState.stage.toLowerCase()) {
                case "production":
                    setTitle("LCCV");
                    document.title = "LCCV";
                    break;
                case "readiness":
                    setTitle("Perun4V");
                    document.title = "Perun4V";
                    break;
                default:
                    setTitle("Perun4V");
                    document.title = "Perun4V";
            }
        } catch (err) {
            toast("Unable to determine environment", false, 3000);
        }
    }, [stageState.stage]);

    useEffect(() => {
        getActiveDirectorySettings()
            .then(settings => {
                if (!settings || !settings.tenantId || !settings.clientId) {
                    toast(
                        "Active Directory settings not found. Please check your application settings.",
                        false,
                        3000
                    );
                    return;
                }

                setMsalConfig({
                    auth: {
                        clientId: settings.clientId,
                        authority: `https://login.microsoftonline.com/${settings.tenantId}/`,
                    },
                });
            })
            .catch(() => toast("Failed to fetch Active Directory settings.", false, 3000));
    }, []);

    useEffect(() => {
        if (!msalInstance) return;

        const msalInit = async () => {
            await msalInstance.initialize();

            msalInstance
                .handleRedirectPromise()
                .then(async res => {
                    if (res !== null) {
                        dispatch(loginRequest({ token: res.idToken }));
                    }
                })
                .catch(err => {
                    console.error(err);
                    toast(`${err.errorMessage}`, false, 5000);
                });
        };

        msalInit();
    }, [msalInstance, dispatch]);

    useEffect(() => {
        if (login.error) {
            toast(login.error, false);
        }
    }, [login.error]);

    const loginClick = async () => {
        if (!msalInstance) return;

        await msalInstance.acquireTokenRedirect(loginRequestObj);
    };

    return (
        <div id="container-center-login" className="container-center">
            <RedirectIfLoggedIn />
            <div id="login-form-container">
                <img id="logo" src={logo} alt="Logo" />
                <h1 id="h1-login-a" className="tsc-blue">
                    {title}
                </h1>
                <Form id="form-login-a">
                    <p id="acceptance-checkbox-login" className="acceptance-checkbox">
                        <Checkbox
                            id="checkbox-login-a"
                            fitted={false}
                            checked={usePolicyAccepted}
                            onChange={() => setUsePolicyAccepted(!usePolicyAccepted)}
                        />
                        <label id="label-login-b">
                            Read and accept the{" "}
                            <strong id="strong-login-a">
                                <a
                                    id="a-tscnetPolicy-login"
                                    href={UseStatementPdf}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    TSCNET Policy
                                </a>
                            </strong>{" "}
                            to be able to login
                        </label>
                    </p>
                    <p id="p-login-c">
                        <label id="label-information-login">
                            Information according to Art. 13 GDPR&nbsp;
                            <strong id="strong-login-d">
                                <a
                                    id="a-login-noopener"
                                    href={Pdf}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    here
                                </a>
                            </strong>
                        </label>
                    </p>
                    <Button
                        id="button-login-b"
                        fluid
                        content="Login"
                        primary
                        loading={login.currentlySending}
                        onClick={loginClick}
                        disabled={!usePolicyAccepted || !msalInstance}
                    />
                    {localLoginEnabled && (
                        <Button
                            id="button-login-c"
                            fluid
                            content="Local Login"
                            primary
                            onClick={() => navigate("/login/local")}
                        />
                    )}
                </Form>
            </div>
        </div>
    );
};
